/**
 * @author: Corey Sanford <corey@hackcapital.com>
 * @date:   2019-01-10
 *
 * Lists available contact filter fields
 */
import {
  CaretDelimitedStringQueryBuilder,
} from '@/Core/utilities/filter-query-builders/CaretDelimitedStringQueryBuilder';
import {
  ExactMatchQueryBuilder,
} from '@/Core/utilities/filter-query-builders/ExactMatchQueryBuilder';
import {
  IntegerQueryBuilder,
} from '@/Core/utilities/filter-query-builders/IntegerQueryBuilder';
import {
  TextQueryBuilder,
} from '@/Core/utilities/filter-query-builders/TextQueryBuilder';
import {
  AddressQueryBuilder,
} from '@/Core/utilities/filter-query-builders/AddressQueryBuilder';
import {
  DatetimeQueryBuilder,
} from '@/Core/utilities/filter-query-builders/DatetimeQueryBuilder';
import { ContactStatusTypes as statuses } from '@/Contacts/config/contact-status-types';
import { Icons } from './contact-create-fields';

import {

  ADDRESSES_NAME,
  ADDRESSES_FIELD,

  ASSIGNED_USER_ID_NAME,
  ASSIGNED_USER_ID_FIELD,

  OWNER_USER_ID_NAME,
  OWNER_USER_ID_FIELD,

  // Specialty fields
  TAGS_FIELD,
  TAGS_NAME,

  HOTNESS_FIELD,
  HOTNESS_NAME,

  // Text fields
  EMAIL_PRIMARY_FIELD,
  EMAIL_SECONDARY_NAME,
  EMAIL_SECONDARY_FIELD,
  SPOUSE_EMAIL_NAME,
  SPOUSE_EMAIL_FIELD,

  PHONE_MOBILE_NAME,
  PHONE_MOBILE_FIELD,
  FAX_NAME,
  FAX_FIELD,
  PHONE_HOME_NAME,
  PHONE_HOME_FIELD,
  PHONE_OFFICE_NAME,
  PHONE_OFFICE_FIELD,
  SPOUSE_PHONE_NAME,
  SPOUSE_PHONE_FIELD,

  // Dates
  CREATED_AT_FIELD,
  CREATED_AT_NAME,
  LAST_EMAIL_TEXT_SENT_AT_FIELD,
  LAST_EMAIL_TEXT_SENT_AT_NAME,
  LAST_ACTIVITY_AT_FIELD,
  LAST_ACTIVITY_AT_NAME,
  LAST_COMMENT_AT_FIELD,
  LAST_COMMENT_AT_NAME,
  LAST_PHONE_ACTIVITY_AT_FIELD,
  LAST_PHONE_ACTIVITY_AT_NAME,
  LAST_SITE_VISIT_AT_FIELD,
  LAST_SITE_VISIT_AT_NAME,
  LAST_USER_LOGIN_AT_FIELD,
  LAST_USER_LOGIN_AT_NAME,
  LAST_SEARCH_PERFORMED_AT_FIELD,
  LAST_SEARCH_PERFORMED_AT_NAME,
  LAST_SAVED_SEARCH_ADDED_AT_FIELD,
  LAST_SAVED_SEARCH_ADDED_AT_NAME,
  LAST_SAVED_SEARCH_REMOVED_AT_FIELD,
  LAST_SAVED_SEARCH_REMOVED_AT_NAME,
  LAST_PROPERTY_VIEWED_AT_FIELD,
  LAST_PROPERTY_VIEWED_AT_NAME,
  LAST_FAVORITE_PROPERTY_ADDED_AT_FIELD,
  LAST_FAVORITE_PROPERTY_ADDED_AT_NAME,
  LAST_FAVORITE_PROPERTY_REMOVED_AT_FIELD,
  LAST_FAVORITE_PROPERTY_REMOVED_AT_NAME,
  NEXT_AP_TASK_RECURRING_AT_FIELD,
  NEXT_AP_TASK_RECURRING_AT_NAME,
  NEXT_TASK_AT_FIELD,
  NEXT_TASK_AT_NAME,
  CAMPAIGN_ACTION_NAME,
  CAMPAIGN_ACTION_FIELD,
  // These fields not listed in the contacts cache table,
  // therefore not filterable or necessary to export
  //
  // TOTAL_AP_TASKS_RECURRING_FIELD,
  // TOTAL_AP_TASKS_RECURRING_NAME,
  // TOTAL_CONTACT_ACTIONS_FIELD,
  // TOTAL_CONTACT_ACTIONS_NAME,
  // TOTAL_OTHER_CONTACTS_FIELD,
  // TOTAL_OTHER_CONTACTS_NAME,
  // Totals
  TOTAL_ACTIVITIES_FIELD,
  TOTAL_ACTIVITIES_NAME,
  TOTAL_AP_TASKS_REGULAR_FIELD,
  TOTAL_AP_TASKS_REGULAR_NAME,
  TOTAL_COMMENTS_FIELD,
  TOTAL_COMMENTS_NAME,
  TOTAL_EMAILS_TEXTS_FIELD,
  TOTAL_EMAILS_TEXTS_NAME,
  TOTAL_PHONE_ACTIVITY_FIELD,
  TOTAL_PHONE_ACTIVITY_NAME,
  TOTAL_SITE_VISITS_FIELD,
  TOTAL_SITE_VISITS_NAME,
  TOTAL_USER_LOGINS_FIELD,
  TOTAL_USER_LOGINS_NAME,
  TOTAL_SEARCHES_PERFORMED_FIELD,
  TOTAL_SEARCHES_PERFORMED_NAME,
  TOTAL_SAVED_SEARCHES_FIELD,
  TOTAL_SAVED_SEARCHES_NAME,
  TOTAL_PROPERTY_VIEWS_FIELD,
  TOTAL_PROPERTY_VIEWS_NAME,
  TOTAL_FAVORITED_PROPERTIES_FIELD,
  TOTAL_FAVORITED_PROPERTIES_NAME,
  TOTAL_RECEIVED_TEXTS_FIELD,
  TOTAL_RECEIVED_TEXTS_NAME,
  TOTAL_SENT_TEXTS_FIELD,
  TOTAL_SENT_TEXTS_NAME,
  TOTAL_TASKS_FIELD,
  TOTAL_TASKS_NAME,
  UNSUBSCRIBED_DATE_FIELD,
  UNSUBSCRIBED_DATE_NAME,

} from './table-headers';


const textFieldAttributes = {
  filterComponent: 'text',
  queryType: 'text',
  icon: Icons.TEXT,
  operators: TextQueryBuilder.OperatorList,
};
const dateFieldAttributes = {
  filterComponent: 'date',
  queryType: 'date-only',
  icon: 'calendar_today',
  operators: DatetimeQueryBuilder.OperatorList,
};
const datetimeFieldAttributes = {
  filterComponent: 'date',
  queryType: 'date',
  icon: 'calendar_today',
  operators: DatetimeQueryBuilder.OperatorList,
};
const integerFieldAttributes = {
  filterComponent: 'integer',
  queryType: 'integer',
  icon: 'looks_5',
  operators: IntegerQueryBuilder.OperatorList,
};
const dollarAmountFieldAttributes = {
  filterComponent: 'integer',
  queryType: 'integer',
  icon: Icons.DOLLAR_AMOUNT,
  operators: IntegerQueryBuilder.OperatorList,
  help: 'Omit dollar signs and commas from your search',
};

const idxFieldAttributes = {
  secondaryIcon: 'public',
};

const MULTISELECT_TYPE = 'multiselect';
const FOREIGNKEY_TYPE = 'foreignkey';

/**
 * Caret delimited fields are searched with a multiselect.
 *
 * The options for the multi-select must be loaded at load-time,
 * using a kind of resolver.
 *
 * @param {string} field like 'tag_names'
 * @param {string} name like 'Tags'
 */
function filterTagsFieldObject(field, name) {
  return {
    name,
    field,
    filterComponent: MULTISELECT_TYPE,
    queryType: 'caret-delimited',
    operators: CaretDelimitedStringQueryBuilder.OperatorList,
    icon: 'label',
    options: [],
    loadWith: 'tags',
  };
}

/**
 * Search for one or more integer foreign keys corresponding
 * to separate resources
 *
 * @param {string} field like 'tag_names'
 * @param {string} name like 'Tags'
 */
function filterForeignKeyFieldObject(field, name, icon, loadWith) {
  return {
    name,
    field,
    filterComponent: FOREIGNKEY_TYPE,
    queryType: 'exact-match',
    operators: ExactMatchQueryBuilder.OperatorList,
    icon,
    options: [],
    loadWith,
  };
}

/**
 * Search for one or more integer foreign keys corresponding
 * to separate resources
 *
 * @param {string} field like 'hotness'
 * @param {string} name like 'Hotness'
 */
function filterHotnessFieldObject(field, name, icon, loadWith) {
  return {
    name,
    field,
    filterComponent: 'hotness',
    queryType: 'hotness',
    operators: ExactMatchQueryBuilder.OperatorList,
    icon,
    options: [],
    loadWith,
  };
}

const customFieldFilterMapping = Object.freeze({
  Text: textFieldAttributes,
  Number: integerFieldAttributes,
  Date: dateFieldAttributes,
  'Dollar Amt': dollarAmountFieldAttributes,
});

/**
 * Given a name, field, and data type, return a custom field filter
 */
export function createCustomFieldFilter({
  name, field, dataType,
}) {
  return {
    name,
    field,
    secondaryIcon: Icons.CUSTOM_FIELD,
    ...customFieldFilterMapping[dataType],
  };
}

/**
 *
 */
function filterTextFieldObject(fields, name, secondaryIcon, help = null) {
  return {
    name,
    field: fields,
    ...textFieldAttributes,
    secondaryIcon,

  };
}
function filterDatetimeFieldObject(field, name) {
  return {
    name,
    field,
    ...datetimeFieldAttributes,
  };
}
function filterIdxDateFieldObject(field, name) {
  return {
    name,
    field,
    ...datetimeFieldAttributes,
    ...idxFieldAttributes,
  };
}

function filterIntegerFieldObject(field, name) {
  return {
    name,
    field,
    ...integerFieldAttributes,
  };
}

function filterIdxIntegerFieldObject(field, name) {
  return {
    name,
    field,
    ...integerFieldAttributes,
    ...idxFieldAttributes,
  };
}

const availableContactFilterFields = [
  filterForeignKeyFieldObject(ASSIGNED_USER_ID_FIELD, ASSIGNED_USER_ID_NAME, Icons.ASSIGNED_USER, 'team-members'),
  filterForeignKeyFieldObject(OWNER_USER_ID_FIELD, OWNER_USER_ID_NAME, Icons.ASSIGNED_USER, 'team-members'),
  filterHotnessFieldObject(HOTNESS_FIELD, HOTNESS_NAME, Icons.HOTNESS, 'hotnesses'),
  filterTagsFieldObject(TAGS_FIELD, TAGS_NAME),

  {
    name: 'Source',
    field: 'source',
    filterComponent: 'multiselect',
    queryType: 'exact-match',
    icon: 'play_for_work',
    options: [],
    loadWith: 'sources',
    operators: ExactMatchQueryBuilder.OperatorList,
  },

  {
    name: 'Status',
    field: 'statusid',
    filterComponent: FOREIGNKEY_TYPE,
    queryType: 'exact-match',
    icon: Icons.STATUS,
    options: statuses.map(status => ({ id: status.id, name: status.name })),
    loadWith: 'statuses',
    operators: ExactMatchQueryBuilder.OperatorList,
  },

  {
    name: 'Type',
    field: 'type',
    filterComponent: 'multiselect',
    queryType: 'exact-match',
    icon: 'person_outline',
    options: [],
    loadWith: 'contact-types',
    operators: ExactMatchQueryBuilder.OperatorList,
  },

  Object.assign(
    filterTextFieldObject(ADDRESSES_FIELD, ADDRESSES_NAME, Icons.ADDRESSES),
    { operators: AddressQueryBuilder.OperatorList },
  ),

  filterTextFieldObject(
    [EMAIL_PRIMARY_FIELD, EMAIL_SECONDARY_FIELD, SPOUSE_EMAIL_FIELD],
    'Any Email Field (3)',
    'email',
  ),
  filterTextFieldObject(EMAIL_PRIMARY_FIELD, 'Email (Primary)', 'email'),
  filterTextFieldObject(EMAIL_SECONDARY_FIELD, EMAIL_SECONDARY_NAME, 'email'),
  filterTextFieldObject(SPOUSE_EMAIL_FIELD, SPOUSE_EMAIL_NAME, 'email'),
  filterTextFieldObject(SPOUSE_EMAIL_FIELD, SPOUSE_EMAIL_NAME, 'email'),

  filterTextFieldObject(
    [PHONE_MOBILE_FIELD, FAX_FIELD, PHONE_HOME_FIELD, PHONE_OFFICE_FIELD, SPOUSE_PHONE_FIELD],
    'Any Phone Field (5)',
    'phone',
  ),
  filterTextFieldObject(PHONE_MOBILE_FIELD, PHONE_MOBILE_NAME, 'phone'),
  filterTextFieldObject(FAX_FIELD, FAX_NAME, 'phone'),
  filterTextFieldObject(PHONE_HOME_FIELD, PHONE_HOME_NAME, 'phone'),
  filterTextFieldObject(PHONE_OFFICE_FIELD, PHONE_OFFICE_NAME, 'phone'),
  filterTextFieldObject(SPOUSE_PHONE_FIELD, SPOUSE_PHONE_NAME, 'phone'),

  filterDatetimeFieldObject(CREATED_AT_FIELD, CREATED_AT_NAME),
  filterDatetimeFieldObject(LAST_EMAIL_TEXT_SENT_AT_FIELD, LAST_EMAIL_TEXT_SENT_AT_NAME),
  filterDatetimeFieldObject(LAST_ACTIVITY_AT_FIELD, LAST_ACTIVITY_AT_NAME),
  filterDatetimeFieldObject(LAST_COMMENT_AT_FIELD, LAST_COMMENT_AT_NAME),
  filterDatetimeFieldObject(LAST_PHONE_ACTIVITY_AT_FIELD, LAST_PHONE_ACTIVITY_AT_NAME),
  filterDatetimeFieldObject(NEXT_AP_TASK_RECURRING_AT_FIELD, NEXT_AP_TASK_RECURRING_AT_NAME),
  filterDatetimeFieldObject(CAMPAIGN_ACTION_FIELD, CAMPAIGN_ACTION_NAME),
  filterDatetimeFieldObject(NEXT_TASK_AT_FIELD, NEXT_TASK_AT_NAME),
  filterIdxDateFieldObject(LAST_SITE_VISIT_AT_FIELD, LAST_SITE_VISIT_AT_NAME),
  filterIdxDateFieldObject(LAST_USER_LOGIN_AT_FIELD, LAST_USER_LOGIN_AT_NAME),
  filterIdxDateFieldObject(LAST_SEARCH_PERFORMED_AT_FIELD, LAST_SEARCH_PERFORMED_AT_NAME),
  filterIdxDateFieldObject(LAST_SAVED_SEARCH_ADDED_AT_FIELD, LAST_SAVED_SEARCH_ADDED_AT_NAME),
  filterIdxDateFieldObject(LAST_SAVED_SEARCH_REMOVED_AT_FIELD, LAST_SAVED_SEARCH_REMOVED_AT_NAME),
  filterIdxDateFieldObject(LAST_PROPERTY_VIEWED_AT_FIELD, LAST_PROPERTY_VIEWED_AT_NAME),
  filterIdxDateFieldObject(UNSUBSCRIBED_DATE_FIELD, UNSUBSCRIBED_DATE_NAME),
  filterIdxDateFieldObject(
    LAST_FAVORITE_PROPERTY_ADDED_AT_FIELD,
    LAST_FAVORITE_PROPERTY_ADDED_AT_NAME,
  ),
  filterIdxDateFieldObject(
    LAST_FAVORITE_PROPERTY_REMOVED_AT_FIELD,
    LAST_FAVORITE_PROPERTY_REMOVED_AT_NAME,
  ),


  filterIntegerFieldObject(TOTAL_ACTIVITIES_FIELD, TOTAL_ACTIVITIES_NAME),
  filterIntegerFieldObject(TOTAL_AP_TASKS_REGULAR_FIELD, TOTAL_AP_TASKS_REGULAR_NAME),
  filterIntegerFieldObject(TOTAL_COMMENTS_FIELD, TOTAL_COMMENTS_NAME),
  filterIntegerFieldObject(TOTAL_EMAILS_TEXTS_FIELD, TOTAL_EMAILS_TEXTS_NAME),
  filterIntegerFieldObject(TOTAL_PHONE_ACTIVITY_FIELD, TOTAL_PHONE_ACTIVITY_NAME),
  filterIntegerFieldObject(TOTAL_RECEIVED_TEXTS_FIELD, TOTAL_RECEIVED_TEXTS_NAME),
  filterIntegerFieldObject(TOTAL_SENT_TEXTS_FIELD, TOTAL_SENT_TEXTS_NAME),
  filterIntegerFieldObject(TOTAL_TASKS_FIELD, TOTAL_TASKS_NAME),
  filterIdxIntegerFieldObject(TOTAL_SITE_VISITS_FIELD, TOTAL_SITE_VISITS_NAME),
  filterIdxIntegerFieldObject(TOTAL_USER_LOGINS_FIELD, TOTAL_USER_LOGINS_NAME),
  filterIdxIntegerFieldObject(TOTAL_SEARCHES_PERFORMED_FIELD, TOTAL_SEARCHES_PERFORMED_NAME),
  filterIdxIntegerFieldObject(TOTAL_SAVED_SEARCHES_FIELD, TOTAL_SAVED_SEARCHES_NAME),
  filterIdxIntegerFieldObject(TOTAL_PROPERTY_VIEWS_FIELD, TOTAL_PROPERTY_VIEWS_NAME),
  filterIdxIntegerFieldObject(TOTAL_FAVORITED_PROPERTIES_FIELD, TOTAL_FAVORITED_PROPERTIES_NAME),

  // These fields not listed in the contacts cache table,
  // therefore not filterable or necessary to export
  // // filterIntegerFieldObject(TOTAL_AP_TASKS_RECURRING_FIELD, TOTAL_AP_TASKS_RECURRING_NAME),
  // // filterIntegerFieldObject(TOTAL_CONTACT_ACTIONS_FIELD, TOTAL_CONTACT_ACTIONS_NAME),
  // // filterIntegerFieldObject(TOTAL_OTHER_CONTACTS_FIELD, TOTAL_OTHER_CONTACTS_NAME),
];

export default availableContactFilterFields;

const mandatoryFilterProperties = [
  'name',
  'field',
  'filterComponent',
  'queryType',
  'icon',
  'operators',
];


/**
 * A couple simple checks to keep the developers in line.
 */
availableContactFilterFields.forEach(fieldDetails => {
  mandatoryFilterProperties.forEach(property => {

    if (!fieldDetails.name) {
      throw new Error(`Woops, a filter is missing its name: ${fieldDetails}`);
    }

    if (!fieldDetails[property]) {
      throw new Error(
        `Must specify the property '${property}' on the '${fieldDetails.name}' contact filter field`,
      );
    }
  });
});
